import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomToggle from '../../../components/toggle';
import { usePostBrokerSsoConfiguration, useWealthlaneCustomerApi } from '../../../hooks';
import useGetBrokerOptions from '../../../hooks/get/useGetBrokerOptions';
import { type ModalStatusType } from '../../../types/common';
import { BROKER_SSO_CONFIGURATION_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';
import FormSelect from '../../../components/dropdown/formSelect';

interface BrokerageSsoConfigurationModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  loading: boolean;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  modalStatus: ModalStatusType;
  id?: string;
}

const BrokerageSsoConfigurationModal = ({
  isModalOpen,
  closeModal,
  initialData,
  setModalStatus,
  loading,
  handleRequest,
  modalStatus,
  id,
}: BrokerageSsoConfigurationModalType): JSX.Element => {
  const { handleRequest: customerHandleRequest } = useWealthlaneCustomerApi();

  const { mutate, isLoading } = usePostBrokerSsoConfiguration(
    handleRequest,
    closeModal,
    initialData.id
  );

  const { data: brokerOptions, isLoading: isBrokerOptionsLoading } =
    useGetBrokerOptions(customerHandleRequest);

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={BROKER_SSO_CONFIGURATION_VALIDATION}
      onSubmit={(values) => {
        mutate(values);
      }}>
      {({ touched, handleChange, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            size='xl'
            valueEdited={_.isEqual(values, initialData.data)}
            title={`${modalStatus} Brokerage SSO Configuration`}
            id={id}
            children={
              <Form id='brokerageSsoConfigurationForm'>
                <ModalBody id='modalBody'>
                  <div className='grid grid-cols-2 gap-4'>
                    <InputText
                      type='text'
                      label='Name'
                      name='name'
                      id='brokerSsoConfigName'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      required
                    />
                  </div>

                  <div className='mt-4'>
                    <div className='grid grid-cols-2 gap-4'>
                      <InputText
                        type='text'
                        label='Issuer'
                        name='configurationData.issuer'
                        id='brokerSsoConfigIssuer'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='Audience'
                        name='configurationData.audience'
                        id='brokerSsoConfigAudience'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='ACS URL'
                        name='configurationData.acsUrl'
                        id='brokerSsoConfigAcsUrl'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                      <InputText
                        type='text'
                        label='Profile'
                        name='configurationData.profile'
                        id='brokerSsoConfigProfile'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                      />
                      <InputText
                        type='text'
                        label='Landing Page'
                        name='configurationData.landingPage'
                        id='brokerSsoConfigLandingPage'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                      />
                    </div>
                  </div>

                  <div className='mt-4'>
                    <CustomToggle
                      label='Active'
                      name='isActive'
                      id='brokerSsoConfigIsActive'
                      checked={values?.isActive}
                      disabled={modalStatus === 'View'}
                      onChange={handleChange}
                    />
                  </div>
                </ModalBody>
                <ModalFooterComponent
                  id='brokerageSsoConfigurationModalFooter'
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default BrokerageSsoConfigurationModal;
